import {
  type ContainerProps,
  type MantineRadius,
  type MantineSpacing,
  Skeleton,
} from '@mantine/core';
import type { CSSProperties, ElementType, ReactNode } from 'react';
import classes from './FlexibleSkeleton.module.css';

export type SkeletonVariant =
  | 'text'
  | 'title'
  | 'card'
  | 'circle'
  | 'avatar'
  | 'button'
  | 'input'
  | 'image'
  | 'custom';

export type FlexDirection = 'row' | 'column' | 'row-reverse' | 'column-reverse';

export type JustifyContent =
  | 'flex-start'
  | 'flex-end'
  | 'center'
  | 'space-between'
  | 'space-around'
  | 'space-evenly';

export type AlignItems =
  | 'flex-start'
  | 'flex-end'
  | 'center'
  | 'stretch'
  | 'baseline';

export type AnimationType = 'default' | 'wave' | 'pulse' | 'shimmer' | 'none';

export type ResponsiveBreakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface SkeletonItemStyle {
  /** Height of skeleton item */
  height?: number | string;
  /** Width of skeleton item */
  width?: number | string;
  /** Minimum width */
  minWidth?: number | string;
  /** Maximum width */
  maxWidth?: number | string;
  /** Minimum height */
  minHeight?: number | string;
  /** Maximum height */
  maxHeight?: number | string;
  /** Border radius */
  radius?: MantineRadius | number;
  /** Make skeleton circular */
  circle?: boolean;
  /** Flex grow property */
  flexGrow?: number;
  /** Flex shrink property */
  flexShrink?: number;
  /** Flex basis property */
  flexBasis?: number | string;
  /** Individual margin */
  margin?: MantineSpacing | number;
  /** Individual padding */
  padding?: MantineSpacing | number;
  /** Custom CSS properties */
  style?: CSSProperties;
  /** Custom CSS class */
  className?: string;
  /** Individual animation override */
  animation?: AnimationType;
}

export interface ResponsiveConfig {
  /** Configuration for mobile devices */
  mobile?: Partial<FlexibleSkeletonProps>;
  /** Configuration for tablet devices */
  tablet?: Partial<FlexibleSkeletonProps>;
  /** Configuration for desktop devices */
  desktop?: Partial<FlexibleSkeletonProps>;
  /** Custom breakpoint configurations */
  breakpoints?: {
    [K in ResponsiveBreakpoint]?: Partial<FlexibleSkeletonProps>;
  };
}

export interface FlexibleSkeletonProps {
  /** Number of skeleton items */
  count?: number;
  /** Flex direction for container */
  direction?: FlexDirection;
  /** Gap between items (in pixels) */
  gap?: number | string;
  /** Predefined skeleton variant */
  variant?: SkeletonVariant;
  /** Custom style for individual skeleton items */
  itemStyle?: SkeletonItemStyle;
  /** Custom style for container */
  containerStyle?: CSSProperties;
  /** Container CSS class */
  containerClassName?: string;
  /** Justify content alignment */
  justifyContent?: JustifyContent;
  /** Align items alignment */
  alignItems?: AlignItems;
  /** Flex wrap behavior */
  flexWrap?: 'wrap' | 'nowrap' | 'wrap-reverse';
  /** Animation type for all skeletons */
  animation?: AnimationType;
  /** Disable animations */
  animate?: boolean;
  /** Custom skeleton items with individual configurations */
  customItems?: SkeletonItemStyle[];
  /** Render function for custom skeleton items */
  renderItem?: (item: SkeletonItemStyle, index: number) => ReactNode;
  /** Show loading state */
  visible?: boolean;
  /** Fade in/out animation duration */
  fadeDuration?: number;
  /** Responsive configurations */
  responsive?: ResponsiveConfig;
  /** Container element type */
  as?: ElementType;
  /** Additional props for container element */
  containerProps?: ContainerProps;
  /** Accessibility label */
  'aria-label'?: string;
  /** Test ID for testing */
  'data-testid'?: string;
  /** Loading completion callback */
  onAnimationComplete?: () => void;
  /** Children to render alongside skeletons */
  children?: ReactNode;
}

const defaultVariantStyles: Record<SkeletonVariant, SkeletonItemStyle> = {
  text: {
    height: 12,
    width: '80%',
    radius: 'xs',
  },
  title: {
    height: 24,
    width: '60%',
    radius: 'sm',
    margin: 'sm',
  },
  card: {
    height: 120,
    width: '100%',
    radius: 'md',
  },
  circle: {
    width: 40,
    height: 40,
    circle: true,
  },
  avatar: {
    width: 48,
    height: 48,
    circle: true,
    radius: 'xl',
  },
  button: {
    height: 36,
    width: 120,
    radius: 'sm',
  },
  input: {
    height: 36,
    width: '100%',
    radius: 'sm',
  },
  image: {
    height: 200,
    width: '100%',
    radius: 'md',
  },
  custom: {},
};

export const FlexibleSkeleton = ({
  count = 1,
  direction = 'column',
  gap = 8,
  variant = 'text',
  itemStyle = {},
  containerStyle = {},
  containerClassName,
  justifyContent = 'flex-start',
  alignItems = 'stretch',
  flexWrap = 'nowrap',
  animation = 'default',
  animate = true,
  customItems,
  renderItem,
  visible = true,
  fadeDuration = 200,
  responsive: _responsive,
  as: ContainerElement = 'div',
  containerProps = {},
  'aria-label': ariaLabel = 'Loading content',
  'data-testid': dataTestId,
  onAnimationComplete,
  children,
}: FlexibleSkeletonProps) => {
  // Get base variant styles
  const baseStyle = defaultVariantStyles[variant];

  // Merge base style with custom item style
  const mergedItemStyle = { ...baseStyle, ...itemStyle };

  // Get animation class
  const getAnimationClass = () => {
    if (!animate || animation === 'none') return '';

    switch (animation) {
      case 'wave':
        return classes.waveAnimation;
      case 'pulse':
        return classes.pulseAnimation;
      case 'shimmer':
        return classes.shimmerAnimation;
      default:
        return '';
    }
  };

  // Container styles
  const containerStyles: CSSProperties = {
    display: 'flex',
    flexDirection: direction,
    gap: typeof gap === 'number' ? `${gap}px` : gap,
    justifyContent,
    alignItems,
    flexWrap,
    opacity: visible ? 1 : 0,
    transition: fadeDuration
      ? `opacity ${fadeDuration}ms ease-in-out`
      : undefined,
    ...containerStyle,
  };

  // Render individual skeleton item
  const renderSkeletonItem = (itemConfig: SkeletonItemStyle, index: number) => {
    const {
      height,
      width,
      minWidth,
      maxWidth,
      minHeight,
      maxHeight,
      radius,
      circle,
      flexGrow,
      flexShrink,
      flexBasis,
      margin,
      padding,
      style: individualStyle,
      className: individualClassName,
      animation: individualAnimation,
    } = itemConfig;

    const skeletonStyle: CSSProperties = {
      flexGrow,
      flexShrink,
      flexBasis,
      minWidth,
      maxWidth,
      minHeight,
      maxHeight,
      margin: typeof margin === 'number' ? `${margin}px` : margin,
      padding: typeof padding === 'number' ? `${padding}px` : padding,
      ...individualStyle,
    };

    const animationClass = individualAnimation
      ? individualAnimation === 'wave'
        ? classes.waveAnimation
        : individualAnimation === 'pulse'
          ? classes.pulseAnimation
          : individualAnimation === 'shimmer'
            ? classes.shimmerAnimation
            : ''
      : getAnimationClass();

    const combinedClassName = [
      classes.skeletonItem,
      animationClass,
      individualClassName,
    ]
      .filter(Boolean)
      .join(' ');

    if (renderItem) {
      return renderItem(itemConfig, index);
    }

    return (
      <Skeleton
        key={index}
        height={height}
        width={width}
        radius={radius}
        circle={circle}
        animate={animate && (individualAnimation || animation) === 'default'}
        style={skeletonStyle}
        className={combinedClassName}
        onAnimationEnd={index === count - 1 ? onAnimationComplete : undefined}
      />
    );
  };

  // Generate skeleton items
  const skeletonItems =
    customItems && customItems.length > 0
      ? customItems.map((item, index) => renderSkeletonItem(item, index))
      : Array.from({ length: count }, (_, index) =>
          renderSkeletonItem(mergedItemStyle, index),
        );

  const Container = ContainerElement as ElementType;

  return (
    <Container
      style={containerStyles}
      className={`${classes.flexibleSkeleton} ${containerClassName || ''}`}
      aria-label={ariaLabel}
      data-testid={dataTestId}
      role="status"
      aria-live="polite"
      {...containerProps}
    >
      {skeletonItems}
      {children}
    </Container>
  );
};

export default FlexibleSkeleton;
